import { throttle } from 'lodash';
import { useState, useRef, useEffect, useMemo } from 'react';
import Sticky from 'react-stickynode';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  primaryShadow,
  zIndices,
  useBreakpoint,
} from '@hivebrite/design-system/Theme';

import { useUIContext } from 'hb-react/frontoffice/contexts/UIContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import useGroupNavigationTheme from 'hb-react/shared/hooks/theme/useGroupNavigationTheme';
import { ImmersiveMenu } from 'hb-react/shared/hooks/useImmersiveMenu';

import Menu from '../Menu';
import SocialLinks from '../SocialLinks';

const TopicNavigationRow = ({ socialLinks, topic, topicMenuRef }) => {
  const { currentNetwork } = useDefaultAppContexts();
  const groupNavigationTheme = useGroupNavigationTheme();
  const breakpoint = useBreakpoint();
  const { headerHeight, headerHeightMobile, setTopicMenuHeight } =
    useUIContext();
  const [isSticked, setIsSticked] = useState(false);
  const [menuHeight, setMenuHeight] = useState();
  const menuBoxRef = useRef();
  const Socials =
    topic?.social_links?.length > 0 ? (
      <SocialLinks
        links={topic.social_links.map((linkId) =>
          socialLinks.getIn(['social_links', linkId.toString()]),
        )}
      />
    ) : null;

  const getHeaderHeight = (isImmersiveMenu, height = 0) =>
    isImmersiveMenu ? 0 : height;

  const getStickyTop = ({ newHeaderHeightMobile, newHeaderHeight }) => {
    const top = breakpoint === 's' ? newHeaderHeightMobile : newHeaderHeight;

    if (typeof headerHeight !== 'number') {
      // For public office.
      const headerEl = document.querySelector('.header-container--nav');

      if (headerEl) {
        const computedStyles = window.getComputedStyle(headerEl);

        return computedStyles.position === 'fixed'
          ? headerEl.getBoundingClientRect().height
          : 0;
      }
    }

    return top;
  };

  const handleStickyStateChange = ({ status }) => {
    setIsSticked(status === Sticky.STATUS_FIXED);
  };

  useEffect(() => {
    const updateMenuHeight = throttle(
      () => {
        const menuRect = menuBoxRef.current?.getBoundingClientRect();

        if (menuRect) {
          setMenuHeight(menuRect.height);
          if (setTopicMenuHeight) {
            setTopicMenuHeight(menuRect.height);
          }
        }
      },
      100,
      { leading: true, trailing: true },
    );

    updateMenuHeight();
    window.addEventListener('resize', updateMenuHeight);

    return () => {
      window.removeEventListener('resize', updateMenuHeight);
    };
  }, []);

  return (
    <Wrapper>
      {breakpoint === 's' ? Socials : null}
      {breakpoint === 's' ? (
        <CoverSmall
          style={{ backgroundImage: `url(${topic.cover_picture_small_url})` }}
        />
      ) : null}
      <ImmersiveMenu location={window.location} network={currentNetwork}>
        {({ isImmersiveMenu }) => {
          const newHeaderHeight = getHeaderHeight(
            isImmersiveMenu,
            headerHeight,
          );
          const newHeaderHeightMobile = getHeaderHeight(
            isImmersiveMenu,
            headerHeightMobile,
          );

          // eslint-disable-next-line react-hooks/rules-of-hooks
          const stickyTop = useMemo(
            () => getStickyTop({ newHeaderHeightMobile, newHeaderHeight }),
            [newHeaderHeight, newHeaderHeightMobile],
          );

          return (
            <>
              {isSticked && breakpoint !== 's' ? (
                <MenuBackground
                  height={menuHeight}
                  top={stickyTop}
                  zIndex={zIndices.positioned + 1}
                  bg={groupNavigationTheme.backgroundColor}
                />
              ) : null}
              <Sticky
                top={stickyTop}
                innerZ={zIndices.positioned + 1}
                onStateChange={handleStickyStateChange}
              >
                <Box ref={menuBoxRef} flexDirection="row" position="relative">
                  <Box flexGrow={1}>
                    <Menu
                      ariaLabel={s__(
                        'TopicAccessibility|Menu of %{groupName}',
                      ).i__({
                        groupName: topic.name,
                      })}
                      customPages={topic.app_pages}
                      ref={topicMenuRef}
                    />
                  </Box>
                  {breakpoint !== 's' ? Socials : null}
                </Box>
              </Sticky>
            </>
          );
        }}
      </ImmersiveMenu>
    </Wrapper>
  );
};

export default TopicNavigationRow;

const CoverSmall = styled(Box).attrs({
  width: '100vw',
  maxWidth: '100%',
})`
  background-size: cover;
  background-position: center;
  aspect-ratio: 757/418;
`;

const Wrapper = styled(Box).attrs({
  mx: { _: 0, m: 6, xl: 0 },
  flexDirection: { _: 'column', m: 'row' },
  justifyContent: 'stretch',
  display: 'block',
})``;

const MenuBackground = styled(Box)`
  position: fixed;
  left: 0;
  right: 0;
  ${primaryShadow}
`;
